import dayjs from "dayjs";
import type { CashAdvancesCustomerSchemas, OffersSchemas } from "api-types";
import { fmtAmount, fmtAmountWithoutDecimals } from "shared-utils";
import { logger } from "@wayflyer/blackbox-fe";

/**
 * A helper function to determine whether the amount should be displayed with decimals or not.
 * Expect that once an agreed currency display format is decided, a common function within core can replace this.
 * If the last 2 digits are 00, the amount is displayed without decimals.
 * Otherwise, the full amount is displayed with decimals.
 *
 * @param amountInLowestDenomination - The amount in the lowest denomination.
 * @param currency - The currency symbol.
 * @returns The formatted offers amount.
 */
export const fmtAmountCommon = (amountInLowestDenomination: number, currency: string): string => {
  if (amountInLowestDenomination % 100 === 0) {
    return fmtAmountWithoutDecimals(amountInLowestDenomination, currency);
  }
  return fmtAmount(amountInLowestDenomination, currency);
};

export const fmtFixedCollectionAmount = (
  dailyCollectionFixedInLowestDenomination: number,
  deploymentCurrency: string,
  collectionFrequency: string
): string => {
  if (collectionFrequency === "monthly") {
    return fmtAmountCommon(dailyCollectionFixedInLowestDenomination * 30, deploymentCurrency);
  }

  if (collectionFrequency === "biweekly") {
    return fmtAmountCommon(dailyCollectionFixedInLowestDenomination * 14, deploymentCurrency);
  }

  if (collectionFrequency === "weekly") {
    return fmtAmountCommon(dailyCollectionFixedInLowestDenomination * 7, deploymentCurrency);
  }

  return fmtAmountCommon(dailyCollectionFixedInLowestDenomination, deploymentCurrency);
};

export const daysToMonths = (days: number): number => {
  const now = dayjs();
  const later = now.add(days, "days");
  return Math.round(later.diff(now, "months", true));
};

export const collectionFrequencyToDays = (collectionFrequency: OffersSchemas.CollectionFrequency): number => {
  switch (collectionFrequency) {
    case "daily":
      return 1;
    case "weekly":
      return 7;
    case "biweekly":
      return 14;
    case "monthly":
      return 30;
  }
};

export const combineOfferAndCashAdvance = (
  offer: OffersSchemas.OfferDisplaySchema,
  cashAdvance?: CashAdvancesCustomerSchemas.CashAdvanceSummarySchema
): CashAdvancesCustomerSchemas.TrancheCollectionParamsIn => {
  if (cashAdvance) {
    if (cashAdvance.collectionFrequency !== offer.collectionFrequency) {
      throw new Error(
        `Cash advance ${cashAdvance.id} collection frequency (${cashAdvance.collectionFrequency}) does not match the new offer (${offer.collectionFrequency}).`
      );
    }
    if (cashAdvance.collectionMode !== offer.collectionMode) {
      // This is only an issue if there is already more than one tranche on the cash advance
      if (cashAdvance.tranches.length !== 1) {
        throw new Error(
          `Cash advance ${cashAdvance.id} collection mode (${cashAdvance.collectionMode}) does not match the new offer (${offer.collectionMode}).`
        );
      }
    }
    if (cashAdvance.collectionCurrency !== offer.collectionCurrency) {
      throw new Error(
        `Cash advance ${cashAdvance.id} collection currency (${cashAdvance.collectionCurrency}) does not match the new offer (${offer.collectionCurrency}).`
      );
    }
  }

  const offerTranches: CashAdvancesCustomerSchemas.TrancheParamsIn[] = offer.tranches
    .filter((tranche) => {
      if (!tranche.dailyCollectionFixedInLowestDenomination) {
        logger.warn(
          `Offer ${offer.id} has tranches with no daily collection fixed amount. Removing tranches from collection schedule calculation.`
        );
        return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (a.startDate === b.startDate) {
        return 0;
      }
      return dayjs(a.startDate).diff(dayjs(b.startDate));
    })
    .map(
      (tranche, index): CashAdvancesCustomerSchemas.TrancheParamsIn => ({
        trancheNk: String(index + 1),
        dailyCollectionFixedInLowestDenomination: tranche.dailyCollectionFixedInLowestDenomination!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        outstandingBalanceInLowestDenomination: 0,
        totalRemittanceInLowestDenomination: tranche.advanceInLowestDenomination + tranche.feeInLowestDenomination,
        deploymentDate: tranche.startDate,
      })
    );

  const existingTranches =
    cashAdvance?.tranches
      .filter((tranche) => {
        if (!tranche.remittanceAmountInLowestDenomination || !tranche.deploymentDate) {
          logger.warn(
            `Cash advance ${cashAdvance?.id} has tranches with no daily collection fixed amount. Removing tranches from collection schedule calculation.`
          );
          return false;
        }
        return true;
      })
      .sort((a, b) => {
        if (a.deploymentDate === b.deploymentDate) {
          return 0;
        }
        return dayjs(a.deploymentDate).diff(dayjs(b.deploymentDate));
      })
      .map(
        (tranche): CashAdvancesCustomerSchemas.TrancheParamsIn => ({
          trancheNk: tranche.trancheNk,
          dailyCollectionFixedInLowestDenomination: tranche.remittanceAmountInLowestDenomination!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
          outstandingBalanceInLowestDenomination: tranche.outstandingBalanceInLowestDenomination,
          totalRemittanceInLowestDenomination: tranche.advanceInLowestDenomination + tranche.feeInLowestDenomination,
          deploymentDate: tranche.deploymentDate!, // eslint-disable-line @typescript-eslint/no-non-null-assertion
        })
      ) ?? [];

  return {
    projectionStartDate:
      cashAdvance?.nextDailyCollectableDate ?? dayjs(offer.tranches[0].startDate).format("YYYY-MM-DD"), // Start the projection from the tranche deployment date
    collectionFrequency: collectionFrequencyToDays(offer.collectionFrequency),
    collectionMode: offer.collectionMode,
    nextCollectionStartDate:
      cashAdvance?.nextCollectionStartDate ?? dayjs(offer.tranches[0].startDate).add(1, "day").format("YYYY-MM-DD"), // Start the projection from the day after the tranche deployment date
    collectionCurrency: offer.collectionCurrency as CashAdvancesCustomerSchemas.Currency,
    tranches: [...existingTranches, ...offerTranches],
  };
};
